package com.yet.tetris.database

import app.cash.sqldelight.Query
import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.SqlDriver
import com.yet.tetris.domain.model.game.TetrominoType
import kotlin.Any
import kotlin.Boolean
import kotlin.Long
import kotlin.String

public class CurrentGameStateQueries(
  driver: SqlDriver,
  private val CurrentGameStateAdapter: CurrentGameState.Adapter,
) : SuspendingTransacterImpl(driver) {
  public fun <T : Any> getGameState(mapper: (
    id: Long,
    score: Long,
    linesCleared: Long,
    level: Long,
    piecesPlaced: Long,
    maxCombo: Long,
    tetrisesCleared: Long,
    tSpinClears: Long,
    perfectClears: Long,
    hardDrops: Long,
    hardDropCells: Long,
    softDropCells: Long,
    backToBackChain: Long,
    isTSpinEligible: Boolean,
    currentPieceType: TetrominoType?,
    currentPieceRotation: Long,
    currentPositionX: Long,
    currentPositionY: Long,
    nextPieceType: TetrominoType,
    nextPieceRotation: Long,
    nextQueue: String,
    holdPieceType: TetrominoType?,
    holdPieceRotation: Long,
    canHold: Boolean,
    isGameOver: Boolean,
    isPaused: Boolean,
    boardWidth: Long,
    boardHeight: Long,
  ) -> T): Query<T> = Query(-2_044_056_006, arrayOf("CurrentGameState"), driver, "CurrentGameState.sq", "getGameState", "SELECT CurrentGameState.id, CurrentGameState.score, CurrentGameState.linesCleared, CurrentGameState.level, CurrentGameState.piecesPlaced, CurrentGameState.maxCombo, CurrentGameState.tetrisesCleared, CurrentGameState.tSpinClears, CurrentGameState.perfectClears, CurrentGameState.hardDrops, CurrentGameState.hardDropCells, CurrentGameState.softDropCells, CurrentGameState.backToBackChain, CurrentGameState.isTSpinEligible, CurrentGameState.currentPieceType, CurrentGameState.currentPieceRotation, CurrentGameState.currentPositionX, CurrentGameState.currentPositionY, CurrentGameState.nextPieceType, CurrentGameState.nextPieceRotation, CurrentGameState.nextQueue, CurrentGameState.holdPieceType, CurrentGameState.holdPieceRotation, CurrentGameState.canHold, CurrentGameState.isGameOver, CurrentGameState.isPaused, CurrentGameState.boardWidth, CurrentGameState.boardHeight FROM CurrentGameState WHERE id = 1") { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getLong(1)!!,
      cursor.getLong(2)!!,
      cursor.getLong(3)!!,
      cursor.getLong(4)!!,
      cursor.getLong(5)!!,
      cursor.getLong(6)!!,
      cursor.getLong(7)!!,
      cursor.getLong(8)!!,
      cursor.getLong(9)!!,
      cursor.getLong(10)!!,
      cursor.getLong(11)!!,
      cursor.getLong(12)!!,
      cursor.getBoolean(13)!!,
      cursor.getString(14)?.let { CurrentGameStateAdapter.currentPieceTypeAdapter.decode(it) },
      cursor.getLong(15)!!,
      cursor.getLong(16)!!,
      cursor.getLong(17)!!,
      CurrentGameStateAdapter.nextPieceTypeAdapter.decode(cursor.getString(18)!!),
      cursor.getLong(19)!!,
      cursor.getString(20)!!,
      cursor.getString(21)?.let { CurrentGameStateAdapter.holdPieceTypeAdapter.decode(it) },
      cursor.getLong(22)!!,
      cursor.getBoolean(23)!!,
      cursor.getBoolean(24)!!,
      cursor.getBoolean(25)!!,
      cursor.getLong(26)!!,
      cursor.getLong(27)!!
    )
  }

  public fun getGameState(): Query<CurrentGameState> = getGameState(::CurrentGameState)

  public fun hasSavedState(): Query<Boolean> = Query(-1_349_979_789, arrayOf("CurrentGameState"), driver, "CurrentGameState.sq", "hasSavedState", "SELECT COUNT(*) > 0 FROM CurrentGameState WHERE id = 1") { cursor ->
    cursor.getBoolean(0)!!
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun insertOrReplaceGameState(
    score: Long,
    linesCleared: Long,
    level: Long,
    piecesPlaced: Long,
    maxCombo: Long,
    tetrisesCleared: Long,
    tSpinClears: Long,
    perfectClears: Long,
    hardDrops: Long,
    hardDropCells: Long,
    softDropCells: Long,
    backToBackChain: Long,
    isTSpinEligible: Boolean,
    currentPieceType: TetrominoType?,
    currentPieceRotation: Long,
    currentPositionX: Long,
    currentPositionY: Long,
    nextPieceType: TetrominoType,
    nextPieceRotation: Long,
    nextQueue: String,
    holdPieceType: TetrominoType?,
    holdPieceRotation: Long,
    canHold: Boolean,
    isGameOver: Boolean,
    isPaused: Boolean,
    boardWidth: Long,
    boardHeight: Long,
  ): Long {
    val result = driver.execute(-1_762_033_224, """
        |INSERT OR REPLACE INTO CurrentGameState (
        |    id, score, linesCleared, level, piecesPlaced, maxCombo, tetrisesCleared, tSpinClears,
        |    perfectClears, hardDrops, hardDropCells, softDropCells, backToBackChain, isTSpinEligible,
        |    currentPieceType, currentPieceRotation,
        |    currentPositionX, currentPositionY, nextPieceType, nextPieceRotation, nextQueue,
        |    holdPieceType, holdPieceRotation, canHold, isGameOver, isPaused, boardWidth, boardHeight
        |) VALUES (1, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 27) {
          var parameterIndex = 0
          bindLong(parameterIndex++, score)
          bindLong(parameterIndex++, linesCleared)
          bindLong(parameterIndex++, level)
          bindLong(parameterIndex++, piecesPlaced)
          bindLong(parameterIndex++, maxCombo)
          bindLong(parameterIndex++, tetrisesCleared)
          bindLong(parameterIndex++, tSpinClears)
          bindLong(parameterIndex++, perfectClears)
          bindLong(parameterIndex++, hardDrops)
          bindLong(parameterIndex++, hardDropCells)
          bindLong(parameterIndex++, softDropCells)
          bindLong(parameterIndex++, backToBackChain)
          bindBoolean(parameterIndex++, isTSpinEligible)
          bindString(parameterIndex++, currentPieceType?.let { CurrentGameStateAdapter.currentPieceTypeAdapter.encode(it) })
          bindLong(parameterIndex++, currentPieceRotation)
          bindLong(parameterIndex++, currentPositionX)
          bindLong(parameterIndex++, currentPositionY)
          bindString(parameterIndex++, CurrentGameStateAdapter.nextPieceTypeAdapter.encode(nextPieceType))
          bindLong(parameterIndex++, nextPieceRotation)
          bindString(parameterIndex++, nextQueue)
          bindString(parameterIndex++, holdPieceType?.let { CurrentGameStateAdapter.holdPieceTypeAdapter.encode(it) })
          bindLong(parameterIndex++, holdPieceRotation)
          bindBoolean(parameterIndex++, canHold)
          bindBoolean(parameterIndex++, isGameOver)
          bindBoolean(parameterIndex++, isPaused)
          bindLong(parameterIndex++, boardWidth)
          bindLong(parameterIndex++, boardHeight)
        }.await()
    notifyQueries(-1_762_033_224) { emit ->
      emit("CurrentGameState")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun clearGameState(): Long {
    val result = driver.execute(712_926_435, """DELETE FROM CurrentGameState WHERE id = 1""", 0).await()
    notifyQueries(712_926_435) { emit ->
      emit("CurrentGameState")
    }
    return result
  }
}
